<i18n lang="yaml">
en:
  sort: 'Sort'
  withConsumerGradeCPUs: 'With consumer-grade CPUs'
  withEnterpriseGradeCPUs: 'With enterprise-grade CPUs'
  inStock: 'In stock'
  inStockChips: 'In stock'
  location: 'Location'
  spb: 'St. Petersburg'
  msk: 'Moscow'
  tariffPlan: 'Tariff plan'
  discount: 'discount'
  price: 'Price'
  pricePerDay: 'per day'
  pricePerMonth: 'per month'
  priceFrom: 'from'
  priceUpTo: 'up to'
  cpu: 'CPU'
  cores: 'Cores'
  ramText: 'RAM, GB'
  tags: 'Tags'
  2psu: '(2 PSU) 2 power supplies'
  2psuChips: '2 PSU'
  gpu: '(GPU) Graphics Processing Unit'
  gpuChips: 'GPU'
  diskType: 'Disk type'
  selectAll: 'Select all'
  numberOfCores: 'Cores'
  memorySize: 'RAM'
  showAll: 'Show all'
  buildServer: 'Build your server'
  showOtherServers: 'Show other servers'
  filters: 'Filters'
  resetFilters: 'Reset filters'
  resetBtnText: 'Reset'
  applyBtnText: 'Apply'
  noResultsTitle: "We don't have a configuration for the specified parameters"
  noResultsMessagePart1: 'Contact us on'
  noResultsMessagePart2: 'we will find a server to meet your requirements'
  noResultsMessagePart3: 'Or you can build your own server'
  noResultsMessagePart4: 'in the configurator'
ru:
  sort: 'Сортировать'
  withConsumerGradeCPUs: 'С серверными процессорами'
  withEnterpriseGradeCPUs: 'С десктопными процессорами'
  inStock: 'В наличии'
  inStockChips: 'Серверы в наличии'
  location: 'Регион'
  spb: 'Санкт-Петербург'
  msk: 'Москва'
  tariffPlan: 'Тарифный план'
  discount: 'скидка'
  price: 'Цена'
  pricePerDay: 'в день'
  pricePerMonth: 'в месяц'
  priceFrom: 'От'
  priceUpTo: 'До'
  cpu: 'Процессор'
  cores: 'Ядра, количество'
  ramText: 'Объем памяти, ГБ'
  tags: 'Особенности конфигурации'
  2psu: '2 блока питания'
  2psuChips: '2 блока питания'
  gpu: 'Графический ускоритель'
  gpuChips: 'Графический ускоритель'
  diskType: 'Диски'
  selectAll: 'Выбрать все'
  numberOfCores: 'Количество ядер'
  memorySize: 'Объем памяти'
  showAll: 'Показать все'
  buildServer: 'Собрать свой сервер'
  showOtherServers: 'Смотреть другие серверы'
  filters: 'Фильтры'
  resetFilters: 'Сбросить фильтры'
  resetBtnText: 'Сбросить'
  applyBtnText: 'Применить'
  noResultsTitle: 'У нас нет конфигурации по заданным параметрам'
  noResultsMessagePart1: 'Напишите нам на'
  noResultsMessagePart2: 'подберем сервер под вашу задачу'
  noResultsMessagePart3: 'Или соберите свой'
  noResultsMessagePart4: 'в конфигураторе'
</i18n>

<template>
  <div class="servers-configurator__wrapper">
    <div class="row">
      <div
        v-if="isLoading"
        class="servers-configurator__sorting-filter col-xl-9 ml-auto"
      >
        <a-skeleton
          active
          :title="false"
          :paragraph="{
            rows: 1,
            width: '100%'
          }"
        ></a-skeleton>
      </div>
      <div
        v-else
        class="servers-configurator__sorting-filter col-xl-9 ml-auto"
      >
        <div class="servers-configurator__sort">
          <button
            v-if="$nuxt.$breakpoints.sLg"
            v-body-scroll-lock="isSortingShown && $nuxt.$breakpoints.sSm"
            class="servers-configurator__filter-control servers-configurator__filter-control--sorting"
            :class="{'servers-configurator__filter-control--sorting-active' : isSortingShown}"
            type="button"
            @click="isSortingShown = !isSortingShown"
          >
            {{ currentSortType[$i18n.locale].shortTitle }}
            <svg-arrow-up
              v-if="currentSortType[$i18n.locale].direction"
              class="servers-configurator__filter-control-direction"
              :class="{'servers-configurator__filter-control-direction--down' : currentSortType[$i18n.locale].direction === 'down'}"
            ></svg-arrow-up>
            <svg-sorting
              v-if="!currentSortType[$i18n.locale].direction"
              class="servers-configurator__filter-control-icon"
            ></svg-sorting>
          </button>
          <div
            class="servers-configurator__sort-wrapper"
            :class="{'servers-configurator__sort-wrapper--shown' : isSortingShown}"
          >
            <div
              v-if="$nuxt.$breakpoints.sSm"
              class="servers-configurator__sort-header"
            >
              <span class="servers-configurator__sort-title h3">
                {{ $t('sort') }}:
              </span>
              <button
                class="servers-configurator__sort-close"
                type="button"
                @click="isSortingShown = !isSortingShown"
              >
                <svg-close></svg-close>
              </button>
            </div>
            <span class="servers-configurator__sort-title">
              {{ $t('sort') }}:
            </span>
            <dedicated-configurations-sorting
              :current-sort-type="currentSortType[$i18n.locale].type"
              :is-sale="isSale"
              :is-chipcore="isChipcore"
              @changeSortType="(selectedSortType) => currentSortType[$i18n.locale] = selectedSortType"
            ></dedicated-configurations-sorting>
          </div>
        </div>
        <button
          v-if="$nuxt.$breakpoints.sLg"
          class="servers-configurator__filter-control servers-configurator__filter-control--opening"
          type="button"
          @click="isFilterShown = !isFilterShown"
        >
          {{ $t('filters') }}
          <span
            v-if="numberActiveFilterGroups"
            class="servers-configurator__filter-control-counter"
          >
            ({{ numberActiveFilterGroups }})
          </span>
          <svg-filter class="servers-configurator__filter-control-icon"></svg-filter>
        </button>
        <span class="servers-configurator__amount-configurations">
          <template v-if="isShowServersAll">
            {{ translations.configurationsAmount[$i18n.locale]({count: filteredServers.length}) }}
          </template>
          <template v-else>
            {{ translations.configurationsAmount[$i18n.locale]({count: filteredServersList.length}) }}
          </template>
          </span>
      </div>
      <div class="col-xl-9 ml-auto">
        <div
          v-if="hasChips && !isLoading && !$nuxt.$breakpoints.sLg"
          class="chips"
        >
          <button
            v-if="isChipcoreServers"
            class="chips__item"
            type="button"
            @click="isChipcoreServers = false"
          >
            {{ $t('withEnterpriseGradeCPUs') }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="isNotChipcoreServers"
            class="chips__item"
            type="button"
            @click="isNotChipcoreServers = false"
          >
            {{ $t('withConsumerGradeCPUs') }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="isStock"
            class="chips__item"
            type="button"
            @click="isStock = false"
          >
            {{ $t('inStockChips') }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="hasTwoPsu"
            class="chips__item"
            type="button"
            @click="hasTwoPsu = false"
          >
            {{ $t('2psuChips') }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="hasGpu"
            class="chips__item"
            type="button"
            @click="hasGpu = false"
          >
            {{ $t('gpuChips') }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="selectedTariffPlan && selectedTariffPlan !== 'default'"
            class="chips__item"
            type="button"
            @click="selectedTariffPlan = 'default'"
          >
            {{ currentTariffPlanText }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="minPrice || maxPrice"
            type="button"
            class="chips__item"
            @click="minPrice = null, maxPrice = null"
          >
            <template v-if="minPrice && !maxPrice">
              {{ $t('priceFrom') }} {{ $priceFormatter(minPrice) }}
            </template>
            <template v-if="maxPrice && !minPrice">
              {{ $t('priceUpTo') }} {{ $priceFormatter(maxPrice) }}
            </template>
            <template v-if="minPrice && maxPrice">
              {{ $priceFormatter(minPrice) }} - {{ $priceFormatter(maxPrice) }}
            </template>
            <svg-close></svg-close>
          </button>
          <template v-if="hasChips">
            <span
              v-for="(chipsGroup, i) in chips"
              :key="i"
            >
              <template v-if="chipsGroup === chips.locations && chips.locations.length > 0">
                <button
                  v-for="(item, j) in chipsGroup"
                  :key="j"
                  class="chips__item"
                  type="button"
                  @click="deleteChips(i, j)"
                >
                  {{ LocationDictionary[item] }}
                  <svg-close></svg-close>
                </button>
              </template>
              <template v-else>
                <button
                  v-for="(item, j) in chipsGroup"
                  :key="j"
                  class="chips__item"
                  type="button"
                  @click="deleteChips(i, j)"
                >
                  {{ item }}
                  <svg-close></svg-close>
                </button>
              </template>
            </span>
          </template>
          <button
            v-if="settingVCpuRangeSettings.isChangingVCpuRange"
            class="chips__item"
            type="button"
            @click="resetVCpuRange"
          >
            {{ $t('numberOfCores') }}: {{ vCpuRangeModel[0] }} - {{ vCpuRangeModel[1] }}
            <svg-close></svg-close>
          </button>
          <button
            v-if="settingRamSizeRangeSettings.isChangingRamSizeRange"
            class="chips__item"
            type="button"
            @click="resetRamSizeRange"
          >
            {{ $t('memorySize') }}: {{ ramSizeRangeModel[0] }} - {{ ramSizeRangeModel[1] }}
            <svg-close></svg-close>
          </button>
          <button
            class="chips__reset-btn"
            type="button"
            data-gtmid="gtm-selectel-server-fix-filter-reset-filter"
            @click="resetServersForm"
          >
            {{ $t('resetFilters') }}
          </button>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xl-3">
        <div
          class="servers-configurator__filter"
          :class="{'servers-configurator__filter--shown' : isFilterShown}"
        >
          <div
            v-body-scroll-lock="isFilterShown && $nuxt.$breakpoints.sLg"
            class="servers-configurator__filter-wrapper"
          >
            <div class="servers-configurator__filter-body">
              <div
                v-if="$nuxt.$breakpoints.sLg && !isLoading"
                class="servers-configurator__filter-header"
              >
                  <span class="servers-configurator__filter-title h3">
                    {{ $t('filters') }}
                  </span>
                <button
                  class="servers-configurator__filter-close"
                  type="button"
                  @click="changeFilterVisibility"
                >
                  <svg-close></svg-close>
                </button>
              </div>
              <div class="servers-configurator__filter-inner">
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="false"
                    :paragraph="!isSale && !isChipcore ? setSkeletonSettings(4) : !isSale ? setSkeletonSettings(2) : setSkeletonSettings(1)"
                  ></a-skeleton>
                  <template v-else>
                    <div
                      v-if="!isSale && !isChipcore"
                      class="servers-configurator__filter-checkbox servers-configurator__filter-checkbox--group"
                    >
                      <a-checkbox
                        v-model="isNotChipcoreServers"
                        class="servers-configurator__filter-checkbox--group-title"
                      >
                        {{ $t('withConsumerGradeCPUs') }}
                      </a-checkbox>
                    </div>

                    <div
                      v-if="!isSale && !isChipcore"
                      class="servers-configurator__filter-checkbox servers-configurator__filter-checkbox--group"
                    >
                      <a-checkbox
                        v-model="isChipcoreServers"
                        class="servers-configurator__filter-checkbox--group-title"
                      >
                        {{ $t('withEnterpriseGradeCPUs') }}
                      </a-checkbox>
                    </div>

                    <div
                      class="servers-configurator__filter-checkbox">
                      <a-checkbox
                        v-model="isStock"
                        class="servers-configurator__filter-checkbox--group-title"
                        @change="$gtmSendEvent('gtm-selectel-server-fix-filter-in-stock')"
                      >
                        {{ $t('inStock') }}
                      </a-checkbox>
                    </div>
                  </template>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="!isChipcore ? setSkeletonSettings(2) : setSkeletonSettings(1)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('tags') }}
                      </span>
                    </template>
                    <template #chips>
                      <div
                        v-if="hasTwoPsu || hasGpu"
                        class="chips"
                      >
                        <span
                          v-if="hasTwoPsu && hasGpu"
                          class="chips__item"
                        >
                          {{ $t('2psu') }},
                        </span>
                        <span
                          v-if="hasTwoPsu && !hasGpu"
                          class="chips__item"
                        >
                          {{ $t('2psu') }}
                        </span>
                        <span
                          v-if="hasGpu"
                          class="chips__item"
                        >
                          {{ $t('gpu') }}
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <div class="servers-configurator__filter-checkboxes">
                        <div
                          v-if="!isChipcore"
                          class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            v-model="hasTwoPsu"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-configuration-features-2psu')"
                          >
                            {{ $t('2psu') }}
                          </a-checkbox>
                        </div>
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            v-model="hasGpu"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-configuration-features-gpu')"
                          >
                            {{ $t('gpu') }}
                          </a-checkbox>
                        </div>
                      </div>
                    </template>
                  </filter-group>
                </div>
                <div
                  v-if="!isChipcore"
                  class="servers-configurator__filter-group"
                >
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(2)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('location') }}
                      </span>
                    </template>
                    <template #chips>
                      <div
                        v-if="chips.locations && chips.locations.length > 0"
                        class="chips"
                      >
                        <span
                          v-for="(item, i) in chips.locations"
                          :key="i"
                          class="chips__item"
                        >
                          <template v-if="i !== chips.locations.length - 1">
                            {{ LocationDictionary[item] }},
                          </template>
                          <template v-else>
                            {{ LocationDictionary[item] }}
                          </template>
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <a-checkbox-group
                        v-model="selectedLocations"
                        class="servers-configurator__filter-checkboxes"
                      >
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            value="spb"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-location-st-petersburg')"
                          >
                            {{ $t('spb') }}
                          </a-checkbox>
                        </div>
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            value="msk"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-location-moscow')"
                          >
                            {{ $t('msk') }}
                          </a-checkbox>
                        </div>
                      </a-checkbox-group>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="false"
                    :paragraph="setSkeletonSettings(1)"
                  ></a-skeleton>
                  <filter-group v-else-if="!isLoading && $nuxt.$breakpoints.sLg">
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('tariffPlan') }}
                      </span>
                    </template>
                    <template #chips>
                      <div class="chips">
                        <span
                          v-if="selectedTariffPlan && selectedTariffPlan !== 'default'"
                          class="chips__item"
                        >
                          {{ currentTariffPlanText }}
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <a-radio-group
                        v-model="selectedTariffPlan"
                        class="servers-configurator__filter-radios"
                        @change="$gtmSendEvent('gtm-selectel-server-fix-filter-tariff-plan')"
                      >
                        <a-radio
                          class="servers-configurator__filter-radio servers-configurator__filter-radio--hidden"
                          value="default"
                        >
                          {{ $t('tariffPlan') }}
                        </a-radio>
                        <a-radio
                          v-for="plan in currentTariffPlanList"
                          :key="plan.uuid"
                          class="servers-configurator__filter-radio"
                          :value="plan.name"
                        >
                          <template v-if="plan.type === 'day'">
                            {{ translations.dayAmount[$i18n.locale]({count: plan.period}) }}
                          </template>
                          <template v-if="plan.type === 'month'">
                            {{ translations.monthAmount[$i18n.locale]({count: plan.period}) }}
                            <template v-if="plan.discount">
                              - {{ $t('discount') }} {{ plan.discount }}%
                            </template>
                          </template>
                        </a-radio>
                      </a-radio-group>
                    </template>
                  </filter-group>
                  <filter-group v-else>
                    <template #inner>
                      <a-select
                        v-model="selectedTariffPlan"
                        class="select select--wide"
                        default-value="default"
                        @dropdownVisibleChange="$gtmSendEvent('gtm-selectel-server-fix-filter-tariff-plan')"
                      >
                        <a-select-option value="default">
                          {{ $t('tariffPlan') }}
                        </a-select-option>
                        <a-select-option
                          v-for="plan in currentTariffPlanList"
                          :key="plan.uuid"
                          :value="plan.name"
                        >
                          <template v-if="plan.type === 'day'">
                            {{ translations.dayAmount[$i18n.locale]({count: plan.period}) }}
                          </template>
                          <template v-if="plan.type === 'month'">
                            {{ translations.monthAmount[$i18n.locale]({count: plan.period}) }}
                            <template v-if="plan.discount">
                              - {{ $t('discount') }} {{ plan.discount }}%
                            </template>
                          </template>
                        </a-select-option>
                      </a-select>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(1)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('price') }}
                        <template v-if="currentTariffPlanPeriod === 'day'">
                          {{ $t('pricePerDay') }}, ₽
                        </template>
                        <template v-if="currentTariffPlanPeriod === 'month'">
                          {{ $t('pricePerMonth') }}, ₽
                        </template>
                      </span>
                    </template>
                    <template #chips>
                      <div class="chips">
                        <span
                          v-if="minPrice || maxPrice"
                          class="chips__item"
                        >
                          <template v-if="minPrice && !maxPrice">
                            {{ $t('priceFrom') }} {{ $priceFormatter(minPrice) }}
                          </template>
                          <template v-if="maxPrice && !minPrice">
                            {{ $t('priceUpTo') }} {{ $priceFormatter(maxPrice) }}
                          </template>
                          <template v-if="minPrice && maxPrice">
                            {{ $priceFormatter(minPrice) }} - {{ $priceFormatter(maxPrice) }}
                          </template>
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <div class="servers-configurator__filter-price">
                        <div class="servers-configurator__filter-price-item">
                          <a-input-number
                            v-model="minPrice"
                            class="servers-configurator__filter-price-field ant-input-number--without-arrows ant-input-number--from"
                            :class="{'ant-input-number--en' : $i18n.locale === 'en'}"
                            :min="0"
                            :formatter="value => inputNumberValidate(value)"
                            :parser="value => inputNumberValidate(value)"
                          ></a-input-number>
                        </div>
                        <div class="servers-configurator__filter-price-item">
                          <a-input-number
                            v-model="maxPrice"
                            class="servers-configurator__filter-price-field ant-input-number--without-arrows ant-input-number--before"
                            :class="{'ant-input-number--en' : $i18n.locale === 'en'}"
                            :min="0"
                            :formatter="value => inputNumberValidate(value)"
                            :parser="value => inputNumberValidate(value)"
                          ></a-input-number>
                        </div>
                      </div>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(2)"
                  ></a-skeleton>
                  <filter-group
                    v-else
                    :is-visually-hidden="true"
                  >
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('cpu') }}
                      </span>
                    </template>
                    <template #chips>
                      <div
                        v-if="processorsChips.flat().length > 0"
                        class="chips"
                      >
                        <span
                          v-for="(processor, i) in processorsChips.flat()"
                          :key="i"
                          class="chips__item"
                        >
                          <template v-if="i !== processorsChips.flat().length - 1">
                            {{ processor }};
                          </template>
                          <template v-else>
                            {{ processor }}
                          </template>
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <div
                        v-if="filteredServersByProcessor.intel && filteredServersByProcessor.intel.length > 0"
                        class="servers-configurator__filter-subgroup"
                      >
                        <filter-sub-group>
                          <template #title>
                            <span
                              class="servers-configurator__filter-subgroup-title"
                              data-gtmid="gtm-selectel-server-fix-filter-cpu-intel"
                            >
                              Intel
                            </span>
                          </template>
                          <template #inner>
                            <div
                              ref="IntelCheckboxesBox"
                              class="servers-configurator__filter-subgroup-accordion"
                            >
                              <simplebar
                                class="servers-configurator__filter-subgroup-checkboxes"
                                data-simplebar
                                data-simplebar-auto-hide="false"
                                style="max-height: 100%;"
                              >
                                <a-checkbox
                                  ref="IntelCheckboxSelectingAll"
                                  class="servers-configurator__filter-checkbox"
                                  :indeterminate="indeterminateIntel"
                                  :checked="isSelectedAllIntelProcessors"
                                  @change="selectAllIntelProcessors"
                                >
                                  {{ $t('selectAll') }}
                                </a-checkbox>
                                <a-checkbox-group
                                  ref="IntelCheckboxes"
                                  v-model="checkedNamesIntel"
                                  class="servers-configurator__filter-checkbox-group"
                                  :options="planOptionsIntel"
                                  @change="selectIntelProcessor"
                                ></a-checkbox-group>
                              </simplebar>
                            </div>
                          </template>
                        </filter-sub-group>
                      </div>

                      <div
                        v-if="filteredServersByProcessor.amd && filteredServersByProcessor.amd.length > 0"
                        class="servers-configurator__filter-subgroup"
                      >
                        <filter-sub-group>
                          <template #title>
                            <span
                              class="servers-configurator__filter-subgroup-title"
                              data-gtmid="gtm-selectel-server-fix-filter-cpu-amd"
                            >
                              AMD
                            </span>
                          </template>
                          <template #inner>
                            <div
                              ref="AmdCheckboxesBox"
                              class="servers-configurator__filter-subgroup-accordion"
                            >
                              <simplebar
                                class="servers-configurator__filter-subgroup-checkboxes"
                                data-simplebar
                                data-simplebar-auto-hide="false"
                                style="max-height: 100%;"
                              >
                                <a-checkbox
                                  ref="AmdCheckboxSelectingAll"
                                  class="servers-configurator__filter-checkbox"
                                  :indeterminate="indeterminateAmd"
                                  :checked="isSelectedAllAmdProcessors"
                                  @change="selectAllAmdProcessors"
                                >
                                  {{ $t('selectAll') }}
                                </a-checkbox>
                                <a-checkbox-group
                                  ref="AmdCheckboxes"
                                  v-model="checkedNamesAmd"
                                  class="servers-configurator__filter-checkbox-group"
                                  :options="planOptionsAmd"
                                  @change="selectAmdProcessor"
                                ></a-checkbox-group>
                              </simplebar>
                            </div>
                          </template>
                        </filter-sub-group>
                      </div>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(1)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('cores') }}
                      </span>
                    </template>
                    <template #chips>
                      <div class="chips">
                        <span
                          v-if="settingVCpuRangeSettings.isChangingVCpuRange"
                          class="chips__item"
                        >
                          {{ vCpuRangeModel[0] }} - {{ vCpuRangeModel[1] }}
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <div
                        ref="rangeVCpu"
                        class="servers-configurator__filter-range-wrap"
                      >
                        <a-slider
                          v-model="vCpuRangeModel"
                          class="servers-configurator__filter-range"
                          :default-value="settingVCpuRangeSettings.defaultVCpuRange"
                          :tooltip-visible="true"
                          :min="settingVCpuRangeSettings.min"
                          :max="settingVCpuRangeSettings.max"
                          :step="null"
                          :auto-focus="true"
                          :marks="settingVCpuRangeSettings.marks"
                          range
                          :get-tooltip-popup-container="() => $refs.rangeVCpu"
                          data-gtmid="gtm-selectel-server-fix-filter-core"
                          @afterChange="(value) => {vCpuRange = value; $gtmSendEvent('gtm-selectel-server-fix-filter-core')}"
                        ></a-slider>
                      </div>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(1)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('ramText') }}
                      </span>
                    </template>
                    <template #chips>
                      <div class="chips">
                        <span
                          v-if="settingRamSizeRangeSettings.isChangingRamSizeRange"
                          class="chips__item"
                        >
                          {{ ramSizeRangeModel[0] }} - {{ ramSizeRangeModel[1] }}
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <div
                        ref="rangeRamSize"
                        class="servers-configurator__filter-range-wrap"
                      >
                        <a-slider
                          v-model="ramSizeRangeModel"
                          class="servers-configurator__filter-range"
                          :default-value="settingRamSizeRangeSettings.defaultRamSizeRange"
                          :tooltip-visible="true"
                          :min="settingRamSizeRangeSettings.min"
                          :max="settingRamSizeRangeSettings.max"
                          :step="null"
                          :marks="settingRamSizeRangeSettings.marks"
                          range
                          :get-tooltip-popup-container="() => $refs.rangeRamSize"
                          data-gtmid="gtm-selectel-server-fix-filter-ram"
                          @afterChange="(value) => {ramSizeRange = value; $gtmSendEvent('gtm-selectel-server-fix-filter-ram')}"
                        >
                        </a-slider>
                      </div>
                    </template>
                  </filter-group>
                </div>
                <div class="servers-configurator__filter-group">
                  <a-skeleton
                    v-if="isLoading"
                    active
                    :title="{
                      width: '30%'
                    }"
                    :paragraph="setSkeletonSettings(3)"
                  ></a-skeleton>
                  <filter-group v-else>
                    <template #title>
                      <span class="servers-configurator__filter-group-title">
                        {{ $t('diskType') }}
                      </span>
                    </template>
                    <template #chips>
                      <div class="chips">
                        <span
                          v-for="(diskType, i) in chips.diskTypes"
                          :key="i"
                          class="chips__item"
                        >
                          <template v-if="i !== chips.diskTypes.length - 1">
                            {{ diskType }},
                          </template>
                          <template v-else>
                            {{ diskType }}
                          </template>
                        </span>
                      </div>
                    </template>
                    <template #inner>
                      <a-checkbox-group
                        v-model="diskTypes"
                        class="servers-configurator__filter-checkboxes"
                      >
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            value="SSD"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-disks-ssd')"
                          >
                            SSD
                          </a-checkbox>
                        </div>
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            value="HDD"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-disks-hdd')"
                          >
                            HDD
                          </a-checkbox>
                        </div>
                        <div class="servers-configurator__filter-checkbox">
                          <a-checkbox
                            value="NVMe"
                            @change="$gtmSendEvent('gtm-selectel-server-fix-filter-disks-nvme')"
                          >
                            NVMe
                          </a-checkbox>
                        </div>
                      </a-checkbox-group>
                    </template>
                  </filter-group>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!isLoading && $nuxt.$breakpoints.sLg"
            class="servers-configurator__filter-footer"
          >
            <button
              class="btn btn--outline btn--sm"
              :disabled="!hasChips"
              type="button"
              data-gtmid="gtm-selectel-server-fix-filter-reset-filter"
              @click="resetServersForm"
            >
              {{ $t('resetBtnText') }}
            </button>
            <button
              class="btn btn--dark btn--sm"
              :disabled="!hasChips"
              type="button"
              @click="changeFilterVisibility"
            >
              {{ $t('applyBtnText') }}
            </button>
          </div>
        </div>
      </div>

      <div class="col-xl-9">
        <div
          v-if="isLoading"
          class="servers-configurator__servers"
        >
          <a-skeleton
            v-for="item in amountSkeletonItems"
            :key="item"
            active
            class="servers-configurator__servers-skeleton"
            :title="{
              width: '100%'
            }"
            :paragraph="{
              width: '100%'
            }"
          ></a-skeleton>
        </div>
        <div
          v-else-if="!isLoading && filteredServers.length"
          class="servers-configurator__servers"
        >
          <server-card
            v-for="server in filteredServers"
            :key="server.uuid"
            :server="server"
            :tariff-plan="currentTariffPlan"
            :is-sale="isSale"
            @showModal="showModal"
          ></server-card>

          <div
            v-if="!isShowServersAll"
            class="servers-configurator__servers-btns"
          >
            <button
              class="servers-configurator__show-all btn btn--dark"
              type="button"
              data-gtmid="gtm-selectel-server-fix-show-all"
              @click="isShowServersAll = true"
            >
              <template v-if="isChipcore">
                {{ $t('showAll') }} Chipcore ({{ filteredServersList.length }})
              </template>
              <template v-else>
                {{ $t('showAll') }} ({{ filteredServersList.length }})
              </template>
            </button>

            <nuxt-link
              v-if="isChipcore"
              :to="localePath('/services/dedicated/')"
              class="btn btn--outline"
            >
              {{ $t('showOtherServers') }}
            </nuxt-link>
            <nuxt-link
              v-else
              :to="localePath('/services/dedicated/configurator/')"
              class="btn btn--outline"
            >
              {{ $t('buildServer') }}
            </nuxt-link>
          </div>
        </div>
        <div
          v-else
          class="servers-configurator__no-results"
        >
          <p class="servers-configurator__no-results-title">
            <span class="h3">
              {{ $t('noResultsTitle') }}
            </span>
            {{ $t('noResultsMessagePart1') }}
            <a
              href="mailto:sales@selectel.ru"
              data-gtmid="gtm-selectel-mailto-click"
            >sales@selectel.ru</a>,
            {{ $t('noResultsMessagePart2') }}.
            <br>
            {{ $t('noResultsMessagePart3') }}
            <nuxt-link :to="localePath('/services/dedicated/configurator/')">
              {{ $t('noResultsMessagePart4') }}
            </nuxt-link>
            .
          </p>
          <div class="servers-configurator__no-results-btns">
            <button
              class="servers-configurator__no-results-reset btn btn--dark btn--lg"
              type="button"
              data-gtmid="gtm-selectel-server-fix-filter-reset-filter"
              @click="resetServersForm"
            >
              {{ $t('resetFilters') }}
            </button>
            <nuxt-link
              :to="localePath('/services/dedicated/configurator/')"
              class="btn btn--outline btn--lg"
            >
              {{ $t('buildServer') }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>

    <servers-availability-modal
      :is-visible-modal="isVisibleModal"
      :server="requestedAvaliabilityServer"
      @setModalVisible="setModalVisible"
    ></servers-availability-modal>

    <portal
      v-if="$nuxt.$breakpoints.sLg"
      to="overlay"
    >
      <s-overlay
        class="servers-configurator__overlay"
        :class="[{'servers-configurator__overlay--sorting' : isSortingShown}, {'overlay--shown' : isSortingShown || isFilterShown}]"
        @closeOverlay="closeOverlay"
      ></s-overlay>
    </portal>
  </div>
</template>

<script>
import {State, TariffLine} from '~/rest-client/servers/all/enum'
import {Period} from '~/helpers/enum/period'
import {translations} from '~/helpers/functions/translations'

import FilterGroup from './filter/DedicatedConfigurationsFilterGroup'
import FilterSubGroup from './filter/DedicatedConfigurationsFilterSubGroup'
import InputNumberValidate from '~/mixins/input-number-validate'
import addAdditionalData from '~/mixins/servers'

import ServerCard from '~/components/common/dedicated-servers/ServerCard'

import SvgClose from '~/assets/icons/common/close.svg'
import SvgArrowUp from '~/assets/icons/arrow-up.svg'
import SvgSorting from '~/assets/icons/common/sorting.svg'
import SvgFilter from '~/assets/icons/common/filter.svg'
import ServersAvailabilityModal from '~/components/common/dedicated-servers/ServersAvailabilityModal'
import DedicatedConfigurationsSorting from './filter/DedicatedConfigurationsSorting'

import {mapGetters, mapActions} from 'vuex'

const LocationDictionary = {
  ru: {
    msk: 'Москва',
    spb: 'Санкт-Петербург'
  },
  en: {
    msk: 'Moscow',
    spb: 'St. Petersburg'
  }
}

export default {
  name: 'DedicatedConfigurations',
  components: {
    DedicatedConfigurationsSorting,
    FilterGroup,
    FilterSubGroup,
    ServerCard,
    SvgClose,
    SvgArrowUp,
    SvgSorting,
    SvgFilter,
    ServersAvailabilityModal
  },
  mixins: [
    InputNumberValidate,
    addAdditionalData
  ],
  props: {
    isSale: {
      type: Boolean,
      default: false
    },
    isChipcore: {
      type: Boolean,
      default: false
    },
    isInTabs: {
      type: Boolean,
      default: false
    }
  },
  async fetch() {
    await this.fetchAllServersPrices()
    await this.fetchPlan()
    await this.fetchLocations()
    this.servers = this.addAdditionalData(this.getAllServersPrices)
    this.tariffPlan = this.getPlan
    this.locations = this.getLocations

    this.isLoading = false
    this.filteredServersByProcessor.intel = this.filterByProcessor('intel')
    this.filteredServersByProcessor.amd = this.filterByProcessor('amd')
  },
  fetchOnServer: false,
  data() {
    return {
      // isShowServersAll: false,
      isShowServersAll: true,
      isLoading: true,
      isSortingShown: false,
      isFilterShown: false,
      hasTwoPsu: false,
      hasGpu: false,
      isChipcoreServers: false,
      isNotChipcoreServers: false,
      isStock: false,
      isVisibleModal: false,
      indeterminateIntel: true,
      indeterminateAmd: true,

      currentPeriod: Period.MONTH,
      selectedTariffPlan: 'default',
      requestedAvaliabilityServer: {
        name: '',
        uuid: ''
      },

      // currentSortType: {
      //   type: 'default',
      //   title: 'по умолчанию',
      //   shortTitle: 'По умолчанию',
      //   direction: null
      // },
      currentSortType: {
        ru: {
          type: this.isSale || this.isChipcore
            ? 'costUp'
            : 'default',
          title: this.isSale || this.isChipcore
            ? 'по стоимости'
            : 'по умолчанию',
          shortTitle: this.isSale || this.isChipcore
            ? 'по цене'
            : 'Сортировать по',
          direction: this.isSale || this.isChipcore
            ? 'up'
            : null
        },
        en: {
          type: this.isSale || this.isChipcore
            ? 'costUp'
            : 'default',
          title: this.isSale || this.isChipcore
            ? 'by Price'
            : 'default',
          shortTitle: this.isSale || this.isChipcore
            ? 'по цене'
            : 'Sort in',
          direction: this.isSale || this.isChipcore
            ? 'up'
            : null
        }
      },
      servers: [],
      locations: [],
      diskTypes: [],
      selectedLocations: [],
      checkedNamesIntel: [],
      checkedNamesAmd: [],
      tariffPlan: [],
      vCpuRange: [],
      vCpuRangeModel: [],
      ramSizeRange: [],
      ramSizeRangeModel: [],
      filteredServersByProcessor: {
        intel: [],
        amd: []
      },

      minPrice: null,
      maxPrice: null,

      processorsRefs: {},

      LocationDictionary: LocationDictionary[this.$i18n.locale],
      TariffLine,
      translations
    }
  },
  computed: {
    ...mapGetters('rest-client/servers/all', ['getAllServersPrices']),
    ...mapGetters('rest-client/servers/plan', ['getPlan']),
    ...mapGetters('rest-client/servers/location', ['getLocations']),
    amountSkeletonItems() {
      const defaultAmountSkeletonItems = 65
      const isSaleAmountSkeletonItems = 25
      const isChipcoreAmountSkeletonItems = 10

      if (this.isSale)
        return isSaleAmountSkeletonItems
      else if (this.isChipcore)
        return isChipcoreAmountSkeletonItems
      else
        return defaultAmountSkeletonItems
    },
    processorsChips() {
      return [this.checkedNamesIntel, this.checkedNamesAmd]
    },
    chips() {
      return {
        locations: this.selectedLocations,
        processorsIntel: this.checkedNamesIntel,
        processorsAmd: this.checkedNamesAmd,
        diskTypes: this.diskTypes
      }
    },
    hasChips() {
      return (
        this.isStock ||
        this.isChipcoreServers ||
        this.isNotChipcoreServers ||
        this.selectedTariffPlan !== 'default' ||
        this.minPrice ||
        this.maxPrice ||
        Object.values(this.chips).flat().length > 0 ||
        this.settingVCpuRangeSettings.isChangingVCpuRange ||
        this.settingRamSizeRangeSettings.isChangingRamSizeRange ||
        this.hasTwoPsu ||
        this.hasGpu
      )
    },

    settingVCpuRangeSettings() {
      const vCpuList = []

      this.filteredServersList.forEach(server => vCpuList.push(server.cpu.coresPerCpu * server.cpu.count))

      const vCpuListUniqueValues = [...new Set(vCpuList)]

      const marks = vCpuListUniqueValues.sort((a, b) => a - b).reduce((acc, elem) => {
        acc[elem] = elem

        return acc
      }, {})

      const min = Math.min(...vCpuListUniqueValues)
      const max = Math.max(...vCpuListUniqueValues)
      const defaultVCpuRange = [min, max]
      const isChangingVCpuRange = this.vCpuRangeModel[0] !== defaultVCpuRange[0] || this.vCpuRangeModel[1] !== defaultVCpuRange[1]

      return {
        marks,
        min,
        max,
        defaultVCpuRange,
        isChangingVCpuRange
      }
    },
    settingRamSizeRangeSettings() {
      const ramSizeList = []

      this.filteredServersList.forEach(server => ramSizeList.push(this.getTotalRamSize(server.ram)))

      const ramSizeListUniqueValues = [...new Set(ramSizeList)]

      const marks = ramSizeListUniqueValues.sort((a, b) => a - b).reduce((acc, elem) => {
        acc[elem] = elem

        return acc
      }, {})

      const min = Math.min(...ramSizeListUniqueValues)
      const max = Math.max(...ramSizeListUniqueValues)
      const defaultRamSizeRange = [min, max]
      const isChangingRamSizeRange = this.ramSizeRangeModel[0] !== defaultRamSizeRange[0] || this.ramSizeRangeModel[1] !== defaultRamSizeRange[1]

      return {
        marks,
        min,
        max,
        defaultRamSizeRange,
        isChangingRamSizeRange
      }
    },
    currentTariffPlanList() {
      return this.tariffPlan.filter(plan => plan.type !== 'free')
    },
    currentTariffPlan() {
      return this.currentTariffPlanList.find(plan => {
        if (this.selectedTariffPlan === 'default') {
          return plan.name === '1 month'
        }

        return plan.name === this.selectedTariffPlan
      })
    },
    currentTariffPlanText() {
      const plan = this.currentTariffPlan

      if (plan) {
        if (plan.type === 'day') {
          return this.translations.dayAmount[this.$i18n.locale]({count: plan.period})
        } else if (plan.type === 'month' && plan.discount) {
          return `${this.translations.monthAmount[this.$i18n.locale]({count: plan.period})} -  ${this.$t('discount')} ${plan.discount}%`
        } else {
          return this.translations.monthAmount[this.$i18n.locale]({count: plan.period})
        }
      }

      return plan
    },
    currentTariffPlanPeriod() {
      return this.selectedTariffPlan === '1 day'
        ? Period.DAY
        : Period.MONTH
    },
    isSelectedAllIntelProcessors() {
      return this.checkedNamesIntel.length === this.planOptionsIntel.length
    },
    isSelectedAllAmdProcessors() {
      return this.checkedNamesAmd.length === this.planOptionsAmd.length
    },
    serversIsAvailable() {
      return this.servers.filter(server => server.isAvailable)
    },
    filteredServers() {
      const countShowingServers = this.$nuxt.$breakpoints.sSm
        ? 5
        : 7
      if (!this.isShowServersAll) {
        const shortFilteredServersList = this.filteredServersList.slice(0, countShowingServers)

        if (this.isSale || this.isChipcore) {
          return shortFilteredServersList.sort(this.sortByType)
        }
        return shortFilteredServersList
      } else {
        return this.filteredServersList
          .filter(server => {
            return (
              this.checkCpuQuantity(server) &&
              this.checkRamSize(server) &&
              this.checkStockLocationWithTariffPlan(server) &&
              this.checkIsStock(server) &&
              this.checkDiskType(server) &&
              this.checkFeatures(server) &&
              this.checkPrice(server) &&
              this.checkProcessor(server) &&
              this.checkIsSale(server) &&
              this.checkIsChipcore(server)
            )
          })
          .sort(this.sortByType)
      }
    },
    filteredServersList() {
      if (this.isSale && !this.isChipcore) {
        return [
          ...this.entryServers,
          ...this.businessServers,
          ...this.amdServers,
          ...this.performanceServers,
          ...this.graphicServers,
          ...this.chipcoreServers,
          ...this.discountServers
        ].filter(server => this.checkIsSale(server))
      }

      if (this.isChipcore) {
        return this.chipcoreServers.filter(server => this.checkIsSale(server))
      }

      return [
        ...this.entryServers,
        ...this.businessServers,
        ...this.amdServers,
        ...this.performanceServers,
        ...this.graphicServers,
        ...this.chipcoreServers,
        ...this.discountServers
      ]
    },
    entryServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.ENTRY)
        .sort(this.sortByPrice)
    },
    businessServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.BUSINESS)
        .sort(this.sortByPrice)
    },
    amdServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.AMD)
        .sort(this.sortByPrice)
    },
    performanceServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.PERFORMANCE)
        .sort(this.sortByPrice)
    },
    graphicServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.GRAPHIC)
        .sort(this.sortByPrice)
    },
    discountServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.DISCOUNT)
        .sort(this.sortByPrice)
    },
    chipcoreServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.CHIPCORE)
        .sort(this.sortByPrice)
    },
    numberOfFilteredServersByProcessor() {
      return Object.values(this.filteredServersByProcessor)
        .map(arr => arr.length)
        .filter(len => len !== 0)
        .length
    },
    planOptionsIntel() {
      return this.filteredServersByProcessor.intel.map(processor => processor.config.cpu.full)
    },
    planOptionsAmd() {
      return this.filteredServersByProcessor.amd.map(processor => processor.config.cpu.full)
    },
    numberActiveFilterGroups() {
      return [
        this.isStock,
        this.isChipcoreServers,
        this.isNotChipcoreServers,
        this.chips.locations.length > 0,
        this.selectedTariffPlan !== 'default',
        !!this.minPrice || !!this.maxPrice,
        this.processorsChips.flat().length > 0,
        this.settingVCpuRangeSettings.isChangingVCpuRange,
        this.settingRamSizeRangeSettings.isChangingRamSizeRange,
        this.hasTwoPsu || this.hasGpu,
        this.diskTypes.length > 0
      ].filter(item => item).length
    }
  },
  watch: {
    filteredServersList() {
      this.$nextTick(() => {
        if (!this.isLoading) {
          this.setRangeSettings()
        }
      })
    },

    // numberActiveFilterGroups() {
    //   this.isShowServersAll = !!this.numberActiveFilterGroups
    // },
    // currentSortType() {
    //   this.isShowServersAll = !(this.currentSortType.type === 'costUp') && this.isSale || !(this.currentSortType.type === 'default') && !this.isSale
    // },

    //TODO
    isLoading() {
      this.$nextTick(() => {
        if (this.filteredServersByProcessor.intel.length > 0)
          this.calculatingProcessorBlockHeight(this.$refs.IntelCheckboxesBox, this.$refs.IntelCheckboxSelectingAll.$el, this.$refs.IntelCheckboxes.$children)
        if (this.filteredServersByProcessor.amd.length > 0)
          this.calculatingProcessorBlockHeight(this.$refs.AmdCheckboxesBox, this.$refs.AmdCheckboxSelectingAll.$el, this.$refs.AmdCheckboxes.$children)
        this.setRangeSettings()
      })
    }
  },
  async mounted() {
    await this.fetchServices()

    if (!this.isLoading) {
      window.addEventListener('resize', this.setProcessorsHeight, true)
    }

    this.$events.$on('dedicatedServersEvent', payload => {
      if (payload === 'isServersWithDesktopProcessors') {
        this.isNotChipcoreServers = false
        this.isChipcoreServers = true
      } else if (payload === 'isServersWithServerProcessors') {
        this.isChipcoreServers = false
        this.isNotChipcoreServers = true
      } else {
        return
      }
    })
  },
  beforeDestroy() {
    if (!this.isLoading) {
      window.removeEventListener('resize', this.setProcessorsHeight, true)
    }

    this.$events.$off('dedicatedServersEvent')
  },
  methods: {
    ...mapActions('rest-client/servers/plan', ['fetchPlan']),
    ...mapActions('rest-client/servers/services', ['fetchServices']),
    ...mapActions('rest-client/servers/all', ['fetchAllServersPrices']),
    ...mapActions('rest-client/servers/location', ['fetchLocations']),
    setSkeletonSettings(numberOfRows, widthRows) {
      return {
        rows: numberOfRows,
        width: widthRows
          ? widthRows
          : '100%'
      }
    },
    setRangeSettings() {
      this.vCpuRange = this.settingVCpuRangeSettings.defaultVCpuRange
      this.vCpuRangeModel = this.settingVCpuRangeSettings.defaultVCpuRange
      this.ramSizeRange = this.settingRamSizeRangeSettings.defaultRamSizeRange
      this.ramSizeRangeModel = this.settingRamSizeRangeSettings.defaultRamSizeRange
    },
    setProcessorsHeight() {
      let timer
      clearTimeout(timer)

      timer = setTimeout(() => {
        this.calculatingProcessorBlockHeight(this.$refs.IntelCheckboxesBox, this.$refs.IntelCheckboxSelectingAll.$el, this.$refs.IntelCheckboxes.$children)
        this.calculatingProcessorBlockHeight(this.$refs.AmdCheckboxesBox, this.$refs.AmdCheckboxSelectingAll.$el, this.$refs.AmdCheckboxes.$children)
      }, 250)
    },
    closeOverlay() {
      this.isSortingShown = false
      this.isFilterShown = false
    },
    changeFilterVisibility() {
      this.isFilterShown = !this.isFilterShown
    },
    resetServersForm() {
      // this.isShowServersAll = false
      this.isShowServersAll = true
      this.currentSortType = {
        ru: {
          type: this.isSale || this.isChipcore
            ? 'costUp'
            : 'default',
          title: this.isSale || this.isChipcore
            ? 'по стоимости'
            : 'по умолчанию',
          shortTitle: this.isSale || this.isChipcore
            ? 'по цене'
            : 'Сортировать по',
          direction: this.isSale || this.isChipcore
            ? 'up'
            : null
        },
        en: {
          type: this.isSale || this.isChipcore
            ? 'costUp'
            : 'default',
          title: this.isSale || this.isChipcore
            ? 'by Price'
            : 'default',
          shortTitle: this.isSale || this.isChipcore
            ? 'по цене'
            : 'Sort in',
          direction: this.isSale || this.isChipcore
            ? 'up'
            : null
        }
      }
      this.resetVCpuRange()
      this.resetRamSizeRange()
      this.hasTwoPsu = false
      this.hasGpu = false
      this.isChipcoreServers = false
      this.isNotChipcoreServers = false
      this.isStock = false
      this.selectedTariffPlan = 'default'
      this.selectedLocations = []
      this.currentPeriod = Period.MONTH
      this.minPrice = null
      this.maxPrice = null
      this.checkedNamesIntel = []
      this.checkedNamesAmd = []
      this.diskTypes = []
    },
    resetVCpuRange() {
      this.vCpuRange = this.settingVCpuRangeSettings.defaultVCpuRange
      this.vCpuRangeModel = this.settingVCpuRangeSettings.defaultVCpuRange
    },
    resetRamSizeRange() {
      this.ramSizeRange = this.settingRamSizeRangeSettings.defaultRamSizeRange
      this.ramSizeRangeModel = this.settingRamSizeRangeSettings.defaultRamSizeRange
    },
    selectIntelProcessor(checkedList) {
      this.indeterminateIntel = !!checkedList.length && checkedList.length < this.planOptionsIntel.length
    },
    selectAllIntelProcessors(evt) {
      this.checkedNamesIntel = evt.target.checked
        ? [...this.planOptionsIntel]
        : []

      this.indeterminateIntel = false
    },
    selectAmdProcessor(checkedList) {
      this.indeterminateAmd = !!checkedList.length && checkedList.length < this.planOptionsAmd.length
    },
    selectAllAmdProcessors(evt) {
      this.checkedNamesAmd = evt.target.checked
        ? [...this.planOptionsAmd]
        : []

      this.indeterminateAmd = false
    },
    filterByProcessor(type) {
      return this.filteredServersList
        .filter(server => {
          return (
            server.isAvailable &&
            server.config.cpu.name &&
            server.config.cpu.name.toLowerCase().indexOf(type) !== -1
          )
        })
        .filter((server, i, servers) => {
          return i === servers.findIndex(item => (
            item.config.cpu.full === server.config.cpu.full
          ))
        })
        .sort(this.sortByPerCpu)
    },
    deleteChips(i, j) {
      this.chips[i].splice(j, 1)
    },
    showModal($event) {
      this.requestedAvaliabilityServer = {
        name: $event.target.getAttribute('data-serverName'),
        uuid: $event.target.getAttribute('data-serverUuid')
      }
      this.isVisibleModal = !this.isVisibleModal
    },
    setModalVisible(isVisibleModal) {
      this.isVisibleModal = isVisibleModal

      this.requestedAvaliabilityServer = this.isVisibleModal
        ? this.requestedAvaliabilityServer
        : {
          name: '',
          uuid: ''
        }
    },
    checkIsStock(server) {
      return this.isStock
        ? !!server.isOrder && this.getAvailable(server.available)
        : true
    },
    getAvailable(available) {
      return available.reduce((isAvailable, available) => {
        return isAvailable || !!available.count
      }, false)
    },
    checkProcessor(server) {
      const processors = this.processorsChips.flat()

      return processors.length !== 0
        ? processors.includes(server.config.cpu.full)
        : true
    },
    calculatingProcessorBlockHeight(checkboxesBox, checkboxSelectingAll, checkboxes) {
      const checkboxSelectingAllHeight = checkboxSelectingAll.offsetHeight + parseInt(getComputedStyle(checkboxSelectingAll).marginBottom)

      if (checkboxes.length >= 5) {
        const checkboxesHeight = checkboxes.slice(0, 5).reduce((height, item) => {
          const checkbox = item.$el
          const checkboxHeight = checkbox.offsetHeight + parseInt(getComputedStyle(checkbox).marginBottom)

          return height += checkboxHeight
        }, 0)

        checkboxesBox.style.height = `${checkboxesHeight + checkboxSelectingAllHeight}px`
      }
    },
    checkCpuQuantity(server) {
      const {cpu} = server

      const vCpuQuantity = cpu.count * cpu.coresPerCpu

      const [minCpuQuantity, maxCpuQuantity] = this.vCpuRange

      return vCpuQuantity >= minCpuQuantity && vCpuQuantity <= maxCpuQuantity
    },
    checkRamSize(server) {
      const {ram} = server

      const [minRamSize, maxRamSize] = this.ramSizeRange

      const totalRamSize = this.getTotalRamSize(ram)

      return totalRamSize >= minRamSize && totalRamSize <= maxRamSize
    },
    checkDiskType(server) {
      const {disk} = server

      if (this.diskTypes.length > 0) {
        return this.diskTypes.some(diskType => {
          return disk.some(disk => disk.type === diskType)
        })
      }

      return true
    },
    checkStockLocationWithTariffPlan(server) {
      const {available} = server
      let checkLocations = true

      const checkTariffPlan = available.some(availableItem => {
        return availableItem.planCount.find(planCount => {
          if (this.isStock) {
            return planCount.planUuid === this.currentTariffPlan.uuid && planCount.count > 0
          } else {
            return planCount.planUuid === this.currentTariffPlan.uuid
          }
        })
      })

      if (this.selectedLocations.length > 0) {
        checkLocations = this.selectedLocations.some(selectedLocation => {
          const currentLocations = this.locations.filter(location => {
            return (
              location.name.substring(0, 3).toLowerCase() === selectedLocation &&
              location.enable
            )
          })

          return currentLocations.some(currentLocation => {
            return available.find(availableItem => {
              const checkTariffPlan = availableItem.planCount.find(planCount => {
                if (this.isStock) {
                  return planCount.planUuid === this.currentTariffPlan.uuid && planCount.count > 0
                } else {
                  return planCount.planUuid === this.currentTariffPlan.uuid
                }
              })

              if (this.isStock) {
                return checkTariffPlan && availableItem.location === currentLocation.uuid && availableItem.count > 0
              } else {
                return checkTariffPlan && availableItem.location === currentLocation.uuid &&
                  (availableItem.count === 0 && server.isPreorder || availableItem.count > 0)
              }
            })
          })
        })
      }

      return checkTariffPlan && checkLocations
    },
    checkFeatures(server) {
      const {gpu} = server

      const hasGpu = this.hasGpu
        ? !!gpu.count
        : true

      const hasTwoPsu = this.hasTwoPsu
        ? server.tagList.map(tag => {if (!tag.isHide) return tag.text.toLowerCase()}).filter(item => item === '2psu').length > 0
        : true

      return hasGpu && hasTwoPsu
    },
    checkPrice(server) {
      const {priceCollection} = server

      const currentPeriodPrice = priceCollection.rub[this.currentTariffPlanPeriod]
      const tariffPlanDiscount = 1 - this.currentTariffPlan.discount / 100

      let isMinPrice
      let isMaxPrice

      if (this.minPrice) {
        if (this.currentTariffPlan.discount) {
          isMinPrice = currentPeriodPrice * tariffPlanDiscount >= this.minPrice
        } else {
          isMinPrice = currentPeriodPrice >= this.minPrice
        }
      } else {
        isMinPrice = true
      }

      if (this.maxPrice) {
        if (this.currentTariffPlan.discount) {
          isMaxPrice = currentPeriodPrice * tariffPlanDiscount <= this.maxPrice
        } else {
          isMaxPrice = currentPeriodPrice <= this.maxPrice
        }
      } else {
        isMaxPrice = true
      }

      return isMinPrice && isMaxPrice
    },
    checkIsSale(server) {
      return this.isSale
        ? server.state === State.DISCOUNT && this.getAvailable(server.available)
        : true
    },
    checkIsChipcore(server) {
      if (this.isChipcoreServers && this.isNotChipcoreServers) {
        return true
      } else if (this.isChipcoreServers && !this.isNotChipcoreServers) {
        return server.tariffLine === TariffLine.CHIPCORE
      } else if (!this.isChipcoreServers && this.isNotChipcoreServers) {
        return server.tariffLine !== TariffLine.CHIPCORE
      } else {
        return true
      }
    },
    getTotalRamSize(ram) {
      return ram.reduce((totalRamSize, ram) => {
        return totalRamSize + ram.size * ram.count
      }, 0)
    },
    sortByType(a, b) {
      switch (this.currentSortType[this.$i18n.locale].type) {
        case 'vCpuUp':
          return (a.cpu.count * a.cpu.coresPerCpu) - (b.cpu.count * b.cpu.coresPerCpu)
        case 'vCpuDown':
          return (b.cpu.count * b.cpu.coresPerCpu) - (a.cpu.count * a.cpu.coresPerCpu)
        case 'ramUp':
          return this.getTotalRamSize(a.ram) - this.getTotalRamSize(b.ram)
        case 'ramDown':
          return this.getTotalRamSize(b.ram) - this.getTotalRamSize(a.ram)
        case 'costUp':
          return a.priceCollection.rub.month - b.priceCollection.rub.month
        case 'costDown':
          return b.priceCollection.rub.month - a.priceCollection.rub.month
      }
    },
    sortByPerCpu(a, b) {
      return b.cpu.coresPerCpu * b.cpu.count - a.cpu.coresPerCpu * a.cpu.count
    },
    sortByPrice(a, b) {
      return a.priceCollection.rub.month - b.priceCollection.rub.month
    }
  }
}
</script>

<style lang="scss">
.chips {
  margin-bottom: 8px;

  @media (max-width: $breakpoint-xl) {
    margin-bottom: 0;
    margin-top: 4px;
    font-size: 0;
  }
}

.chips__item {
  position: relative;
  display: inline-flex;
  margin-right: 8px;
  margin-bottom: 16px;
  padding: 2px 24px 2px 6px;
  font-size: 12px;
  line-height: 16px;
  background-color: rgba($primary, 0.1);
  border: 0;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;

  @media (max-width: $breakpoint-xl) {
    display: inline;
    margin-right: 4px;
    margin-bottom: 0;
    padding: 0;
    color: rgba($primary, 0.4);
    background-color: transparent;
    white-space: normal;
  }

  &:focus {
    outline: none;
  }

  svg {
    z-index: 2;
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    display: block;
    width: 12px;
    height: 12px;
    fill: $primary;

    @media (max-width: $breakpoint-xl) {
      display: none;
    }
  }
}

.chips__reset-btn {
  padding: 0;
  font-size: 12px;
  color: rgba($primary, 0.6);
  background-color: transparent;
  border: 0;
  border-bottom: 1px solid rgba($primary, 0.3);
  cursor: pointer;
  white-space: nowrap;

  &:focus {
    outline: none;
  }
}

.servers-configurator__header {
  @media (max-width: $breakpoint-xl) {
    margin-bottom: 24px;
  }
}

.servers-configurator__subtitle {
  @media (max-width: $breakpoint-xl) {
    max-width: 360px;
    font-size: 16px;
  }
}

.servers-configurator__wrapper {
  padding-top: 16px;
  border-top: 1px solid $grayBorder;

  @media (max-width: $breakpoint-xl) {
    padding-top: 0;
    border-top: 0;
  }

  &--in-tabs {
    padding-top: 24px;
    border-top: 0;
  }

  &--on-server {
    display: none;
  }
}

.servers-configurator__overlay {
  &--sorting {
    @media (min-width: $breakpoint-md + 1) {
      background-color: transparent !important;
    }
  }
}

.servers-configurator__sorting-filter {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: $breakpoint-xl) {
    justify-content: flex-start;
  }

  .ant-skeleton-paragraph {
    margin-bottom: 12px;

    li {
      height: 32px;
    }
  }
}

.servers-configurator__amount-configurations {
  color: rgba($primary, 0.6);

  @media (max-width: $breakpoint-xl) {
    display: block;
    width: 100%;
    margin-bottom: 32px;
    padding-top: 24px;
  }

  @media (max-width: $breakpoint-md) {
    margin-bottom: 16px;
  }
}

.servers-configurator__filter {
  transition: transform 0.3s;
  z-index: 9996;

  @media (max-width: $breakpoint-xl) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40%;
    margin: 0;
    padding-bottom: 70px;
    transform: translateX(100%);
    background-color: #FFF;
    border-left: 1px solid $primary;
  }

  @media (max-width: $breakpoint-md) {
    width: 100%;
    border: 0;
  }

  &--shown {
    transform: translateX(0);
  }
}

.servers-configurator__filter-wrapper {
  @media (max-width: $breakpoint-xl) {
    height: 100%;
    overflow: auto;
  }
}

.servers-configurator__filter-body {

  @media (max-width: $breakpoint-xl) {
    display: flex;
    flex-direction: column;
  }
}

.servers-configurator__filter-header {
  position: relative;
  margin-bottom: 16px;
  padding: 8px 0 8px 10px;
  border-bottom: 1px solid rgba($primary, 0.1);

  @media (max-width: $breakpoint-xl) {
    margin-bottom: 0;
    padding-bottom: 15px;
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.servers-configurator__filter-footer {
  display: flex;
  margin-bottom: 0;
  margin-top: auto;
  padding: 10px;
  border-top: 1px solid $grayBorder;
  font-size: 0;

  @media (max-width: $breakpoint-xl) {
    z-index: 2;
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #FFF;
  }

  .btn {
    flex-grow: 1;

    &:not(:last-child) {
      margin-right: 16px;

      @media (max-width: $breakpoint-md) {
        margin-right: 4px;
      }
    }
  }
}

.servers-configurator__filter-close {
  z-index: 2;
  position: absolute;
  top: 45%;
  right: 6px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  padding: 4px;
  background-color: transparent;
  border: 0;
  transition: 0.3s ease-in-out;

  @media (max-width: $breakpoint-md) {
    width: 24px;
    height: 24px;
  }
}

.servers-configurator__filter-group {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 34px;

  @media (max-width: $breakpoint-xl) {
    margin-bottom: 0;

    &:last-child {
      margin-bottom: 40px;
    }
  }
}

.servers-configurator__filter-group-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.servers-configurator__filter-group-title {
  display: block;
  padding-bottom: 16px;
  color: rgba($primary, 0.65);
  letter-spacing: 0.02em;
  cursor: initial;

  @media (max-width: $breakpoint-xl) {
    margin-bottom: 0;
    padding-top: 12px;
    padding-bottom: 0;
    padding-right: 30px;
    text-transform: none;
    letter-spacing: 0;
    color: $primary;
  }

  .ant-checkbox {
    @media (max-width: $breakpoint-xl) {
      top: 2px;
    }
  }
}

.servers-configurator__filter-group-inner {
  padding-bottom: 6px;

  @media (max-width: $breakpoint-xl) {
    padding-top: 16px;
    padding-bottom: 8px;
  }

  &--visually-hidden {
    max-height: 0;
    padding: 0;
    overflow: hidden;
    visibility: hidden;
    opacity: 0;
  }
}

.servers-configurator__filter-subgroup {
  border-bottom: 1px solid rgba($primary, 0.1);

  @media (max-width: $breakpoint-xl) {
    padding-bottom: 24px;

    &:last-child {
      padding-bottom: 0;
      border-bottom: 0;
    }
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

.servers-configurator__filter-subgroup-title {
  display: block;
  margin-bottom: 12px;
  cursor: pointer;

  @media (max-width: $breakpoint-xl) {
    margin-bottom: 8px;
    color: rgba($primary, 0.6);
  }
}

.servers-configurator__filter-range-wrap {
  position: relative;
  margin: 0 8px;
}

.servers-configurator__filter-range {
  margin-top: 40px;

  @media (max-width: $breakpoint-xl) {
    margin-top: 32px;
  }

  &.ant-slider-with-marks {
    margin-bottom: 0;
  }

  .ant-slider-rail {
    width: calc(100% + 12px);
    left: -6px;
  }

  .ant-slider-dot,
  .ant-slider-mark-text {
    display: none;
  }
}

.servers-configurator__filter-price {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
}

.servers-configurator__filter-price-item {
  .ant-input-number {
    min-width: 100px;
  }
}

.servers-configurator__filter-subgroup-checkboxes {
  padding-top: 12px;

  @media (max-width: $breakpoint-xl) {
    padding-top: 5px;
  }
}

.servers-configurator__filter-checkbox-group {
  .ant-checkbox-group-item:not(:last-child) {
    margin-bottom: 12px;
  }
}

.servers-configurator__filter-checkboxes {
  @media (max-width: $breakpoint-xl) {
    width: 100%;
  }
}

.servers-configurator__filter-checkbox {
  &:not(:last-child) {
    margin-bottom: 12px;
  }

  &--group {
    @media (max-width: $breakpoint-xl) {
      &:not(:last-child) {
        margin-bottom: 0;
      }
    }
  }

  &--group-title {
    @media (max-width: $breakpoint-xl) {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      margin-bottom: 0;
      padding-top: 10px;
      padding-bottom: 10px;
      padding-right: 10px;
      color: $primary;
      border-bottom: 1px solid rgba($primary, 0.1);
    }
  }
}

.servers-configurator__filter-radios {
  width: 100%;
}

.servers-configurator__filter-radio {

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  &--hidden {
    display: none;
  }
}

.servers-configurator__controls {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px 0;
  border-top: 1px solid $grayBorder;

  @media (max-width: $breakpoint-lg) {
    flex-direction: row;
  }
}

.servers-configurator__reset-btn {
  background-color: transparent;
  margin-bottom: 8px;
  padding: 0;
  font-size: 16px;
  color: rgba($primary, 0.6);
  outline: none;
  border: 0;
  cursor: pointer;

  @media (max-width: $breakpoint-lg) {
    margin-bottom: 24px;
  }

  @media (max-width: $breakpoint-sm) {
    display: block;
  }
}

.servers-configurator__filter-control {
  z-index: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  padding-right: 40px;
  text-align: left;
  letter-spacing: 0.02em;
  background-color: #FFF;
  border: 1px solid $primary;
  border-radius: 6px;
  white-space: nowrap;

  &:focus {
    outline: none;
  }

  &:before {
    content: '';
    z-index: 2;
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    display: block;
    width: 40px;
    background-color: #FFF;
    border-radius: 0 6px 6px 0;
  }

  &--sorting {
    min-width: 235px;
    margin-right: 16px;

    @media (max-width: $breakpoint-md) {
      min-width: 0;
      width: 100%;
      margin-right: 0;
    }
  }

  &--sorting-active {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    @media (max-width: $breakpoint-md) {
      border-radius: 6px;
    }
  }

  &--opening {
    min-width: 235px;
    border-radius: 6px;

    @media (max-width: $breakpoint-md) {
      min-width: 0;
      width: calc(50% - 2px);
    }
  }
}

.servers-configurator__filter-control-counter {
  display: inline-block;
  color: rgba($primary, 0.6);
}

.servers-configurator__filter-control-direction {
  z-index: 9;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  fill: $primary;
  transition: transform 0.3s;

  &--down {
    transform: translateY(-50%) rotate(180deg);
  }
}

.servers-configurator__filter-control-icon {
  z-index: 9;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  margin-left: auto;
  margin-right: 0;
}

.servers-configurator__sort {
  position: relative;

  @media (max-width: $breakpoint-md) {
    width: calc(50% - 2px);
    margin-right: 4px;
  }
}

.servers-configurator__sort-wrapper {
  display: inline-flex;
  margin-bottom: 16px;

  @media (max-width: $breakpoint-xl) {
    z-index: 9996;
    position: absolute;
    top: 100%;
    left: 0;
    max-width: 235px;
    min-width: 235px;
    background-color: #FFF;
    border: 1px solid $primary;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    opacity: 0;
    visibility: hidden;
    transition: 0.3s;
  }

  @media (max-width: $breakpoint-md) {
    position: fixed;
    top: auto;
    right: 10px;
    bottom: 0;
    left: 10px;
    flex-direction: column;
    max-width: 100%;
    border-top: 1px solid $primary;
    border-bottom: 0;
    border-radius: 6px 6px 0 0;
    font-size: 16px;
  }

  &--shown {
    margin-bottom: 0;
    opacity: 1;
    visibility: visible;
  }
}

.servers-configurator__sort-header {
  position: relative;
  padding: 8px 0 8px 10px;
  border-bottom: 1px solid $primary;

  .servers-configurator__sort-title {
    @media (max-width: $breakpoint-md) {
      display: block;
      color: $primary;
    }
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.servers-configurator__sort-title {
  margin-right: 16px;
  color: rgba($primary, 0.6);

  @media (max-width: $breakpoint-xl) {
    display: none;
  }
}

.servers-configurator__sort-close {
  z-index: 2;
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 4px;
  background-color: transparent;
  border: 0;
  transition: 0.3s ease-in-out;
}

.servers-configurator__sort-list {
  width: 100%;

  @media (max-width: $breakpoint-xl) {
    padding: 16px 12px 0;
  }
}

.servers-configurator__sort-item {
  display: inline-block;
  margin-right: 16px;
  margin-bottom: 8px;
  cursor: pointer;

  @media (max-width: $breakpoint-xl) {
    display: flex;
    margin-right: 0;
    margin-bottom: 16px;
  }

  &--active {
    input ~ span {
      color: $secondary;
    }
  }

  &--hidden {
    display: none !important;
  }

  input {
    display: none;
  }

  & > span {
    position: relative;
    max-width: calc(100% - 30px);

    @media (max-width: $breakpoint-xs) {
      max-width: 80%;
    }
  }
}

.servers-configurator__sort-item-icon {
  fill: $secondary;
  transition: transform 0.2s;

  @media (max-width: $breakpoint-xl) {
    width: 14px;
    height: 14px;
    margin-left: auto;
    margin-right: 0;
    margin-top: 6px;
  }

  @media (max-width: $breakpoint-md) {
    margin-top: 4px;
    width: 12px;
    height: 12px;
  }

  &--down {
    transform: rotate(180deg);
  }
}

.servers-configurator__servers-skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  padding: 16px;
  border: 1px solid $grayBorder;
  border-radius: 6px;
}

.servers-configurator__servers-line {
  &:not(:last-child) {
    margin-bottom: 40px;

    @media (max-width: $breakpoint-sm) {
      margin-bottom: 32px;
    }
  }
}

.servers-configurator__no-results {
  padding-top: 16px;
  border-top: 1px solid $grayBorder;
}

.servers-configurator__no-results-title {
  margin-bottom: 40px;

  span {
    display: block;
    margin-bottom: 16px;
  }
}

.servers-configurator__servers-btns {
  display: flex;
  flex-wrap: wrap;
  padding-top: 28px;

  @media (max-width: $breakpoint-md) {
    .btn {
      width: 100%;
    }
  }
}

.servers-configurator__reset-btn {
  padding: 0;
  font-size: 16px;
  color: $secondary;
  background-color: transparent;
  border: 0;
  cursor: pointer;
}

.servers-configurator__no-results-btns {
  font-size: 0;

  @media (max-width: $breakpoint-md) {
    .btn {
      width: 100%;
    }
  }
}

.servers-configurator__no-results-reset,
.servers-configurator__show-all {
  margin-right: 8px;

  @media (max-width: $breakpoint-md) {
    margin-right: 0;
    margin-bottom: 8px;
  }
}
</style>